<template>
  <v-dialog v-model="_internalValue" max-width="400px" persistent>
    <v-card v-if="_internalValue" class="pa-6">
      <v-card-title class="pa-2 mb-2 title">
        Escolha abaixo a instância e o balcão de atendimento
      </v-card-title>

      <v-form @submit.prevent="submit">
        <slot name="content">
          <div class="d-flex flex-column pa-2">
            <template v-if="cities.length">
              <ItemSelector
                ref="citySelector"
                v-model="selectors.cities.selected"
                :items="selectors.cities.items"
                :label="selectors.cities.label"
                :placeholder="selectors.cities.placeholder"
                :count="selectors.cities.count"
                :multiple="selectors.cities.multiple"
                :clearable="selectors.cities.clearable"
                :item-value="selectors.cities.itemValue"
                :item-text="selectors.cities.itemText"
                :disabled="loading"
                :rules="_rules"
                @search="selectors.cities.onSearch"
                @paginate="selectors.cities.onPaginate"
              />
            </template>

            <template v-else>
              <ItemSelector
                v-if="_groupField"
                ref="groupSelector"
                class="mt-2"
                v-model="selectors.groups.selected"
                :items="selectors.groups.items"
                :label="selectors.groups.label"
                :placeholder="selectors.groups.placeholder"
                :count="selectors.groups.count"
                :multiple="selectors.groups.multiple"
                :clearable="selectors.groups.clearable"
                :item-value="selectors.groups.itemValue"
                :item-text="selectors.groups.itemText"
                :disabled="loading"
                :rules="_rules"
                @search="selectors.groups.onSearch"
                @paginate="selectors.groups.onPaginate"
              />

              <ItemSelector
                v-if="_subgroupField"
                ref="subgroupSelector"
                class="mt-2"
                v-model="selectors.subgroups.selected"
                :items="selectors.subgroups.items"
                :label="selectors.subgroups.label"
                :placeholder="selectors.subgroups.placeholder"
                :count="selectors.subgroups.count"
                :multiple="selectors.subgroups.multiple"
                :clearable="selectors.subgroups.clearable"
                :item-value="selectors.subgroups.itemValue"
                :item-text="selectors.subgroups.itemText"
                :disabled="!selectors.groups.selected || loading"
                :rules="_rules"
                @search="selectors.subgroups.onSearch"
                @paginate="selectors.subgroups.onPaginate"
              />

              <ItemSelector
                ref="deskSelector"
                class="mt-2"
                v-model="selectors.desks.selected"
                :items="selectors.desks.items"
                :label="selectors.desks.label"
                :placeholder="selectors.desks.placeholder"
                :count="selectors.desks.count"
                :multiple="selectors.desks.multiple"
                :clearable="selectors.desks.clearable"
                :item-value="selectors.desks.itemValue"
                :item-text="selectors.desks.itemText"
                :disabled="!selectors.subgroups.selected || loading"
                :rules="_rules"
                @search="selectors.desks.onSearch"
                @paginate="selectors.desks.onPaginate"
              />
            </template>
          </div>
        </slot>
      </v-form>

      <v-card-actions class="actions">
        <v-btn
          v-if="enabledToClose"
          class="actions-submit text-none"
          width="5.5rem"
          color="red"
          text
          @click="close()"
        >
          Cancelar
        </v-btn>

        <v-btn
          class="actions-submit text-none"
          color="primary"
          width="5.5rem"
          depressed
          :disabled="
            cities.length
              ? !selectors.cities.selected
              : !selectors.groups.selected ||
                !selectors.subgroups.selected ||
                !selectors.desks.selected
          "
          :loading="loading"
          @click="submit"
        >
          Iniciar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import { getJurisdictions } from "@/services/jurisdictions";
import { getCourts } from "@/services/courts";
import { getCounters } from "@/services/counters";
import { handleAlert } from "@/utils";
import ItemSelector from "@/components/itemSelector/ItemSelector.vue";

export default {
  name: "SelectorDialog",

  components: {
    ItemSelector,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    cities: {
      type: Array,
      default: () => [],
    },

    enabledToClose: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(["preSelection"]),

    _internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },

    _rules() {
      return [(v) => !!v || "Campo obrigatório"];
    },

    _groupField() {
      return !this.preSelection?.groupID;
    },

    _subgroupField() {
      if (!this.preSelection?.groupID) return true;

      return !this.preSelection?.subgroupID;
    },
  },

  watch: {
    "selectors.city.selected": {
      handler(value) {
        this.filters.city = value;
      },
      deep: true,
    },
    "selectors.groups.selected": {
      handler(value) {
        this.filters.groups = value;

        if (this._groupField) {
          this.selectors.subgroups.page = 0;
          this.selectors.subgroups.count = 0;
          this.selectors.subgroups.items = [];
          this.selectors.subgroups.selected = null;
        }

        if (value) {
          this.handleFetchSubgroups();
        }
      },
      deep: true,
    },
    "selectors.subgroups.selected": {
      handler(value) {
        this.filters.subgroups = value;
        this.selectors.desks.page = 0;
        this.selectors.desks.count = 0;
        this.selectors.desks.items = [];
        this.selectors.desks.selected = null;

        if (value) {
          this.handleFetchDesks();
        }
      },
      deep: true,
    },
    "selectors.desks.selected": {
      handler(value) {
        this.filters.desks = value;
      },
      deep: true,
    },
  },

  data() {
    return {
      dialog: false,
      filters: {},
      selectors: {
        cities: {
          label: "Cidades",
          placeholder: `Digite para buscar cidades...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          options: [],
          selected: null,
          loading: false,
          multiple: false,
          clearable: false,
          count: 0,
          page: 0,
          search: "",
          onSearch: this.onSearchCities,
          onPaginate: this.fetchMoreCities,
        },
        groups: {
          label: this.$t("group"),
          placeholder: `Digite para buscar ${this.$t("groups")}...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          selected: null,
          loading: false,
          multiple: false,
          clearable: false,
          count: 0,
          page: 0,
          onSearch: this.onSearchGroups,
          onPaginate: this.fetchMoreGroups,
        },
        subgroups: {
          label: this.$t("subgroup"),
          placeholder: `Digite para buscar ${this.$t("subgroups")}...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          selected: null,
          loading: false,
          multiple: false,
          clearable: false,
          count: 0,
          page: 0,
          onSearch: this.onSearchSubgroups,
          onPaginate: this.fetchMoreSubgroups,
        },
        desks: {
          label: this.$t("desk"),
          placeholder: `Digite para buscar ${this.$t("desks")}...`,
          itemValue: "_id",
          itemText: "name",
          items: [],
          selected: null,
          loading: false,
          multiple: false,
          clearable: false,
          count: 0,
          page: 0,
          onSearch: this.onSearchDesks,
          onPaginate: this.fetchMoreDesks,
        },
      },
    };
  },

  beforeMount() {
    this.handlePreSelection();
  },

  methods: {
    submit() {
      if (this.cities.length) {
        if (!this.$refs.citySelector.validate()) return;
      } else {
        const validGroup =
          !this._groupField || this.$refs.groupSelector.validate();
        const validSubgroup =
          !this._subgroupField || this.$refs.subgroupSelector.validate();
        const validDesk = this.$refs.deskSelector.validate();

        if (!validGroup || !validSubgroup || !validDesk) return;
      }

      this.$emit("submit", this.filters);
    },

    emitInput(value) {
      this.$emit("input", value);
    },

    close() {
      this._internalValue = false;

      this.selectors.cities.selected = null;
      this.selectors.groups.selected = null;
      this.selectors.subgroups.selected = null;
      this.selectors.desks.selected = null;
    },

    handlePreSelection() {
      if (this.preSelection.groupID)
        this.selectors.groups.selected = { _id: this.preSelection.groupID };

      if (this.preSelection.groupID && this.preSelection.subgroupID)
        this.selectors.subgroups.selected = {
          _id: this.preSelection.subgroupID,
        };
    },

    async handleFetchGroups(search = "", increment = false) {
      this.selectors.groups.loading = true;

      try {
        const groupID = this.selectors.groups.selected?._id;

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          limit: 10,
          offset: this.selectors.groups.page * 10,
          ...(!!groupID && { nin: [groupID] }),
          ...(!!search?.length && { search }),
        };
        const {
          data,
          headers: { "x-count": count },
        } = await getJurisdictions(payload);

        this.selectors.groups.count = Number(count);

        if (increment) {
          this.selectors.groups.items.push(...data);
        } else {
          this.selectors.groups.items = data;
        }
      } catch (error) {
        this.handleAlert(
          error?.data?.message || "Falha ao obter grupos",
          "error"
        );
      } finally {
        this.selectors.groups.loading = false;
      }
    },

    async handleFetchSubgroups(search = "", increment = false) {
      this.selectors.subgroups.loading = true;

      try {
        const groupID = this.selectors.groups.selected?._id;
        const subgroupID = this.selectors.subgroups.selected?._id;

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          limit: 10,
          offset: this.selectors.subgroups.page * 10,
          ...(!!subgroupID && { nin: [subgroupID] }),
          ...(!!groupID && { groupIDs: [groupID] }),
          ...(!!search?.length && { search }),
        };
        const {
          data,
          headers: { "x-count": count },
        } = await getCourts(payload);

        this.selectors.subgroups.count = Number(count);

        if (increment) {
          this.selectors.subgroups.items.push(...data);
        } else {
          this.selectors.subgroups.items = data;
        }
      } catch (error) {
        this.handleAlert(
          error?.data?.message || "Falha ao obter subgrupos",
          "error"
        );
      } finally {
        this.selectors.subgroups.loading = false;
      }
    },

    async handleFetchDesks(search = "", increment = false) {
      if (!this.selectors.subgroups.selected) {
        return;
      }

      this.selectors.desks.loading = true;

      try {
        const subgroupID = this.selectors.subgroups.selected?._id;
        const deskID = this.selectors.desks.selected?._id;

        const payload = {
          sortBy: "name",
          sortOrder: "asc",
          limit: 10,
          offset: this.selectors.desks.page * 10,
          isActive: true,
          ...(!!deskID && { nin: [deskID] }),
          ...(!!search?.length && { search }),
          ...(!!subgroupID && { subgroupIDs: [subgroupID] }),
        };
        const {
          data,
          headers: { "x-count": count },
        } = await getCounters(payload);

        this.selectors.desks.count = Number(count);

        if (increment) {
          this.selectors.desks.items.push(...data);
        } else {
          this.selectors.desks.items = data;
        }
      } catch (error) {
        this.handleAlert(
          error?.data?.message || "Falha ao obter balcões",
          "error"
        );
      } finally {
        this.selectors.desks.loading = false;
      }
    },

    async handleFetchCities(search = "", increment = false) {
      this.selectors.cities.loading = true;

      try {
        const all = this.selectors.cities.options || [];

        const filtered = search
          ? all.filter((c) =>
              c.text.toLowerCase().includes(search.toLowerCase())
            )
          : all;

        const items = filtered.slice(
          this.selectors.cities.page * 10,
          this.selectors.cities.page * 10 + 10
        );

        if (increment) this.selectors.cities.items.push(...items);
        else this.selectors.cities.items = items;

        this.selectors.cities.count = filtered.length;
      } finally {
        this.selectors.cities.loading = false;
      }
    },

    onSearchGroups(search) {
      this.selectors.groups.page = 0;
      this.selectors.groups.count = 0;
      this.selectors.groups.items = [];

      this.handleFetchGroups(search);
    },

    onSearchSubgroups(search) {
      this.selectors.subgroups.page = 0;
      this.selectors.subgroups.count = 0;
      this.selectors.subgroups.items = [];

      this.handleFetchSubgroups(search);
    },

    onSearchDesks(search) {
      this.selectors.desks.page = 0;
      this.selectors.desks.count = 0;
      this.selectors.desks.items = [];

      this.handleFetchDesks(search);
    },

    fetchMoreGroups(search) {
      this.selectors.groups.page++;
      this.handleFetchGroups(search, true);
    },

    fetchMoreSubgroups(search) {
      this.selectors.subgroups.page++;
      this.handleFetchSubgroups(search, true);
    },

    fetchMoreDesks(search) {
      this.selectors.desks.page++;
      this.handleFetchDesks(search, true);
    },

    onSearchCities(search) {
      this.selectors.cities.search = search;
      this.selectors.cities.page = 0;
      this.handleFetchCities(search, false);
    },

    fetchMoreCities() {
      this.selectors.cities.page++;
      this.handleFetchCities(this.selectors.cities.search, true);
    },

    handleAlert,
  },

  mounted() {
    this.handleFetchGroups();
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
